import { useMemo } from 'react'
import { formatMonth, secondsToMinutes, type InsightsSeriesRow } from '@/lib/asistan'

// Signup volume as bars and billed minutes as a line, bucketed into months.
//
// The endpoint buckets by UTC day because a worker has no idea what timezone the
// admin is sitting in; the browser does, so the days are folded into months here.
// Two series with wildly different units share one chart on purpose — the question
// is "are we adding accounts faster than we are burning minutes", and that is a
// shape question, not a precise one.
interface MonthBucket {
  month: string
  users: number
  minutes: number
  usdMinor: number
}

export default function TrendChart({ series }: { series: InsightsSeriesRow[] }) {
  const buckets = useMemo<MonthBucket[]>(() => {
    const byMonth = new Map<string, MonthBucket>()
    for (const row of series) {
      const month = row.day.slice(0, 7)
      const bucket = byMonth.get(month) ?? { month, users: 0, minutes: 0, usdMinor: 0 }
      bucket.users += row.users ?? 0
      bucket.minutes += secondsToMinutes(row.billed_seconds)
      bucket.usdMinor += row.revenue_usd_minor ?? 0
      byMonth.set(month, bucket)
    }
    return [...byMonth.values()].sort((a, b) => a.month.localeCompare(b.month))
  }, [series])

  if (buckets.length === 0) {
    return <p className="px-6 py-8 text-center text-sm text-gray-400">No signups in range.</p>
  }

  const width = 720
  const height = 200
  const padLeft = 40
  const padBottom = 26
  const padTop = 12
  const plotW = width - padLeft - 12
  const plotH = height - padTop - padBottom
  const maxUsers = Math.max(...buckets.map((b) => b.users), 1)
  const maxMinutes = Math.max(...buckets.map((b) => b.minutes), 1)
  const slot = plotW / buckets.length
  const barW = Math.max(4, Math.min(36, slot * 0.5))

  const linePoints = buckets.map((bucket, i) => {
    const x = padLeft + slot * i + slot / 2
    const y = padTop + plotH - (bucket.minutes / maxMinutes) * plotH
    return `${x},${y}`
  })

  return (
    <div className="px-4 py-4 sm:px-6">
      <div className="flex flex-wrap items-center gap-4 text-xs text-gray-500">
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-sm bg-indigo-400" /> New accounts
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-0.5 w-4 bg-amber-500" /> Billed minutes
        </span>
        <span className="ml-auto">left axis: accounts · right axis: minutes</span>
      </div>
      <div className="mt-3 overflow-x-auto">
        <svg viewBox={`0 0 ${width} ${height}`} className="h-52 w-full min-w-[560px]" role="img" aria-label="New accounts and billed minutes by month">
          {[0, 0.25, 0.5, 0.75, 1].map((tick) => {
            const y = padTop + plotH - tick * plotH
            return (
              <g key={tick}>
                <line x1={padLeft} x2={width - 12} y1={y} y2={y} stroke="#e5e7eb" strokeWidth={1} />
                <text x={padLeft - 6} y={y + 3} textAnchor="end" className="fill-gray-400" fontSize={10}>
                  {Math.round(tick * maxUsers)}
                </text>
                <text x={width - 8} y={y + 3} textAnchor="start" className="fill-amber-500" fontSize={10}>
                  {Math.round(tick * maxMinutes)}
                </text>
              </g>
            )
          })}

          {buckets.map((bucket, i) => {
            const barH = (bucket.users / maxUsers) * plotH
            const x = padLeft + slot * i + (slot - barW) / 2
            const y = padTop + plotH - barH
            return (
              <g key={bucket.month}>
                <rect x={x} y={y} width={barW} height={Math.max(barH, bucket.users > 0 ? 1 : 0)} rx={2} className="fill-indigo-400">
                  <title>{`${formatMonth(bucket.month + '-01')}: ${bucket.users} accounts, ${Math.round(bucket.minutes)} min`}</title>
                </rect>
                {buckets.length <= 18 && (
                  <text x={padLeft + slot * i + slot / 2} y={height - 8} textAnchor="middle" className="fill-gray-400" fontSize={10}>
                    {formatMonth(bucket.month + '-01')}
                  </text>
                )}
              </g>
            )
          })}

          <polyline points={linePoints.join(' ')} fill="none" stroke="#f59e0b" strokeWidth={2} strokeLinejoin="round" />
          {buckets.map((bucket, i) => (
            <circle key={bucket.month} cx={padLeft + slot * i + slot / 2} cy={padTop + plotH - (bucket.minutes / maxMinutes) * plotH} r={2.5} className="fill-amber-500">
              <title>{`${formatMonth(bucket.month + '-01')}: ${Math.round(bucket.minutes)} billed minutes`}</title>
            </circle>
          ))}
        </svg>
      </div>
    </div>
  )
}
